import { compile } from 'path-to-regexp';
import { matchPath } from 'react-router';

import config from '../config';
import { stringify } from './urlHelpers';

const findRouteByName = (nameToFind, routes) => routes.find((route) => route.name === nameToFind);

// New v7-compatible version — register navigate function globally
export const registerNavigate = (navigateFn) => {
  if (typeof window !== 'undefined') {
    window.routerNavigate = navigateFn;
  }
};

export const locationToHref = (location, baseUrl = '') => {
  let href = `${baseUrl}${location.pathname}`;
  if (location.search) href = `${href}?${location.search}`;
  if (location.hash) href = `${href}#${location.hash}`;
  return href;
};

/**
 * E.g. ```const toListingPath = toPathByRouteName('ListingPage', routes);```
 * Then we can generate listing paths with given params (```toListingPath({ id: uuidX })```)
 * @param {string} nameToFind - Route name
 * @param {Array<import('../../types/vansite').VanSite.Route>} routes - Route configuration as flat array
 * @param {string} locale - Locale for the route
 * @returns {Function} - Compiled path function
 */
const toPathByRouteName = (nameToFind, routes, locale) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Path "${nameToFind}" was not found.`);
  }
  // Convert RR7 catch-all /* to path-to-regexp v6 optional wildcard {/*rest}
  const compilablePath = route.path.replace(/\/\*$/, '{/*rest}');
  return compile(`/${locale}${compilablePath}`);
};

/**
 * Shorthand for single path call. (```pathByRouteName('ListingPage', routes, { id: uuidX });```)
 * @param {string} nameToFind - Route name
 * @param {Array<import('../../types/vansite').VanSite.Route>} routes - Route configuration as flat array
 * @param {object} params - Path parameters for the route
 * @param {string} locale - Locale for the route
 * @returns {string} ResourceLocatorString
 */
export const pathByRouteName = (nameToFind, routes, params = {}, locale = config.locale) => {
  const hasEmptySlug = params && params.hasOwnProperty('slug') && params.slug === '';
  const pathParams = hasEmptySlug ? { ...params, slug: 'no-slug' } : params;
  // path-to-regexp v6 expects wildcard (*name) params as non-empty arrays.
  // Convert string to array; remove if empty/falsy (optional wildcard).
  if (pathParams.rest != null) {
    if (!Array.isArray(pathParams.rest)) {
      pathParams.rest = pathParams.rest ? [pathParams.rest] : undefined;
    } else if (pathParams.rest.length === 0) {
      delete pathParams.rest;
    }
  }
  return toPathByRouteName(nameToFind, routes, locale)(pathParams);
};

/**
 * Find the matching routes and their params for the given pathname
 * @param {string} pathname - Full URL path from root with possible
 * search params and hash included
 * @param {Array<{ path: string, exact?: boolean }>} routeConfiguration - Route configuration as flat array
 * @returns {Array<{ route: import('../../types/vansite').VanSite.Route, params: object }>} - All matches as { route, params } objects if matches has
 * exact flag set to false. If not, an array containing just the first matched exact route is returned.
 */
export const matchPathname = (pathname, routeConfiguration) => {
  let pathNameWithoutLocale = pathname.replace(/^\/[a-z]{2}(\/?)/, '');
  pathNameWithoutLocale =
    pathNameWithoutLocale === ''
      ? '/'
      : !pathNameWithoutLocale.startsWith('/')
        ? `/${pathNameWithoutLocale}`
        : pathNameWithoutLocale;
  const matchedRoutes = routeConfiguration.reduce((matches, route) => {
    const { path, exact = true } = route;
    const match = matchPath({ path, end: exact !== false }, pathNameWithoutLocale);
    if (match) {
      matches.push({
        exact,
        route,
        params: match.params || {},
      });
    }
    return matches;
  }, /** @type {Array<{ route: import('../../types/vansite').VanSite.Route, params: object, exact: boolean }>} */ ([]));

  const matchedExactRoute = matchedRoutes.find((r) => {
    return r.exact === true || r.exact == null;
  });

  // We return matched 'exact' path route only if such exists
  // and all matches if no exact flag exists.
  return matchedExactRoute ? [matchedExactRoute] : matchedRoutes;
};

/**
 * ResourceLocatorString is used to direct webapp to correct page.
 * In contrast to Universal Resource Locator (URL), this doesn't contain protocol, host, or port.
 * @param {string} routeName - Name of the route
 * @param {Array<object>} routes - Route configuration as flat array
 * @param {object} pathParams - Path parameters for the route
 * @param {object} searchParams - Search parameters for the route
 * @param {string} locale - Locale for the route
 * @param {string} hash - Hash for the route
 * @returns {string} ResourceLocatorString
 */
export const createResourceLocatorString = (
  routeName,
  routes,
  pathParams = {},
  searchParams = {},
  locale = config.locale,
  hash = ''
) => {
  const searchQuery = stringify(searchParams);
  const includeSearchQuery = searchQuery.length > 0 ? `?${searchQuery}` : '';
  const path = pathByRouteName(routeName, routes, pathParams, locale);
  return `${path}${includeSearchQuery}${hash}`;
};

/**
 * Find component related to route name
 * E.g. `const PageComponent = findComponentByRouteName('CheckoutPage', routes);`
 * Then we can call static methods of given component:
 * `dispatch(PageComponent.setInitialValues({ listing, bookingDates }));`
 * @param {string} nameToFind - Route name
 * @param {Array<import('../../types/vansite').VanSite.Route>} routes - Route configuration as flat array.
 * @returns {import('../../types/vansite').VanSite.Route} - Route that matches the given route name.
 */
export const findRouteByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Component "${nameToFind}" was not found.`);
  }
  return route;
};

/**
 * Replace the leading locale segment of a fully-qualified URL with another locale.
 * Used to build hreflang alternates from a canonical URL. Only rewrites a URL that
 * already carries a locale segment right after the host (e.g. `https://host/de/...`);
 * a locale-less URL is returned unchanged — which is why canonical paths must stay
 * locale-complete for hreflang to resolve to distinct pages.
 * @param {string} url - Fully-qualified URL, e.g. `https://vansite.eu/de/l/{id}`
 * @param {string} newLocale - Target 2-letter locale, e.g. `it`
 * @returns {string} URL with its locale segment swapped to `newLocale`
 */
export const changeLocale = (url, newLocale) =>
  url.replace(/^(https?:\/\/[^/]+)\/[a-z]{2}\//i, `$1/${newLocale}/`);

// A whole two-letter first segment (`/de`, `/de/…`), not the start of `/delivery`.
const LEADING_PATH_LOCALE = /^\/[a-z]{2}(?=\/|$)/;

/**
 * Point an in-app pathname at another locale: swap its leading locale segment for
 * `newLocale`, or prefix `newLocale` when the path has none. Unlike `changeLocale`
 * (fully-qualified URLs, no-op without a locale) this always yields a localized path.
 * @param {string} pathname - Router pathname, e.g. `/de/l/some-van/{id}` or `/l/{id}`
 * @param {string} newLocale - Target 2-letter locale, e.g. `en`
 * @returns {string} e.g. `/en/l/some-van/{id}`; `/de` → `/en`, `/de/` → `/en/`
 */
export const replacePathLocale = (pathname, newLocale) =>
  LEADING_PATH_LOCALE.test(pathname)
    ? pathname.replace(LEADING_PATH_LOCALE, `/${newLocale}`)
    : `/${newLocale}${pathname}`;

/**
 * Get the canonical URL from the given location
 * @param {Array<{ path: string, exact?: boolean }>} routes - Route configuration as flat array
 * @param {object} location - location object from React Router
 * @param {boolean} pathOnly - Whether to return only the pathname
 * @param {string} locale - Locale for the route
 * @returns {string} Canonical URL of the given location
 */
export const canonicalRoutePath = (routes, location, pathOnly = false, locale) => {
  const { pathname, search, hash } = location;

  const pathNameWithoutLocale = pathname.replace(/^\/[a-z]{2}(?=\/)/, '');
  const matches = matchPathname(pathNameWithoutLocale, routes);

  // The leading locale segment (e.g. `/de/`), if the path has one. Real SSR input is
  // always locale-prefixed; we keep that locale in the canonical so every language
  // variant is self-canonical and its hreflang alternates resolve to distinct pages.
  const localeMatch = pathname.match(/^\/([a-z]{2})(?=\/)/);

  const isListingRoute =
    matches.length === 1 &&
    ['ListingPage', 'ListingPageUrlCategory', 'ListingPageCanonical'].includes(
      matches[0].route.name
    );

  if (isListingRoute) {
    // Slugs are mutable (a host can rename a listing), so canonicalise every listing
    // variant to the stable id-based form `/{locale}/l/{id}` — the existing
    // ListingPageCanonical route. The id is always the last path segment.
    const localePrefix = localeMatch ? `/${localeMatch[1]}` : '';
    const segments = pathNameWithoutLocale.replace(/\/$/, '').split('/');
    const id = segments[segments.length - 1];
    const canonicalListingPathname = `${localePrefix}/l/${id}`;
    return pathOnly ? canonicalListingPathname : `${canonicalListingPathname}${search}${hash}`;
  }

  const isLandingRoute = matches.length === 1 && matches[0].route.name === 'LandingPage';

  if (isLandingRoute) {
    // Keep the locale so each localized home page is self-canonical (`/de/`, `/it/`…)
    // rather than collapsing every language onto the locale-less root. Fall back to the
    // render locale; if neither is known (bare root, no locale), leave the path as-is.
    const landingLocale = (localeMatch && localeMatch[1]) || locale;
    if (landingLocale) {
      const canonicalLandingPathname = `/${landingLocale}/`;
      return pathOnly ? canonicalLandingPathname : `${canonicalLandingPathname}${search}${hash}`;
    }
    return pathOnly ? pathname : `${pathname}${search}${hash}`;
  }

  const isStoryblokRoute = matches.length === 1 && matches[0].route.name === 'StoryblokPage';

  if (isStoryblokRoute) {
    return pathOnly ? pathname : `${pathname}${search}${hash}`;
  }

  return pathOnly
    ? pathname.replace(`/${locale}`, '')
    : `${pathname.replace(`/${locale}`, '')}${search}${hash}`;
};

// A return path is only safe to redirect to if it is an in-app, root-relative
// path. Reject protocol-relative (`//host`), backslash (`/\host`) and absolute
// URLs (`https://host`) so a crafted `?from=` cannot turn login into an open
// redirect.
const isSafeReturnPath = (value) =>
  typeof value === 'string' &&
  value.startsWith('/') &&
  !value.startsWith('//') &&
  !value.startsWith('/\\');

/**
 * Resolve where to send the user after authentication.
 *
 * The auth guard (dataRouter.js) redirects unauthenticated users to the login
 * page with the intended destination as a `from` query param. Older/SSR paths
 * may instead pass it via React Router's `location.state.from`. Read the query
 * param first, then fall back to state, and only return values that are safe
 * in-app paths.
 *
 * @param {{search?: string, state?: {from?: string}}} location
 * @returns {string|null} a safe root-relative path, or null when absent/unsafe
 */
export const resolveReturnPath = (location) => {
  if (!location) {
    return null;
  }
  const search = location.search || '';
  const fromQuery = new URLSearchParams(search).get('from');
  const fromState = location.state && location.state.from ? location.state.from : null;
  const candidate = fromQuery || fromState;
  return isSafeReturnPath(candidate) ? candidate : null;
};
